import loadable from "@loadable/component";
import i18n from "i18next";
import { inject } from "mobx-react";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import type { Store } from "../../../../client/stores";
import type { BlockFragment } from "../../generated/webapi.g";
import { Sites_Onair_Day } from "../../generated/webapi.g";
import { daysOfWeek, getCurrentDay, OnAirScheduleInterface } from "../../lib/onair";
import { getOnAirNowByDay, getOnAirSlug } from "../../services/OnAir";
import { Container, Tab, Tabs } from "../../ui";
import { Remote } from "../remote/Remote.component";
import { OnairFallbackMessage } from "./OnairFallbackMessage.component";

const OnAirScheduleDay = loadable(() => import("./OnAirScheduleDay.component"));

interface ScheduleProps extends OnAirScheduleInterface {
    block: BlockFragment;
    store: Store;
}

const Schedule = (props: ScheduleProps) => {
    useEffect(() => {
        const names = [props.current?.name, ...props.schedule.map(s => s.name)].filter(Boolean) as string[];
        const resolved: OnAirScheduleBlockResolved = {
            schedule: {
                names,
            },
        };
        props.store.storeBlock({
            ...props.block,
            value: {
                selected_day: props.selectedDay.full,
            },
            resolved,
        });
    });

    return (
        <OnAirScheduleDay
            currentShow={props.current}
            selectedDay={props.selectedDay}
            schedule={props.schedule}
            timezone={props.siteTimeZone}
        />
    );
};

interface OnAirScheduleProps {
    block: BlockFragment;
    store?: Store;
    params?: ReturnType<typeof useParams>;
}

export interface OnAirScheduleBlockResolved {
    schedule: {
        names: string[];
    };
}

export const OnAirSchedule = inject("store")((props: OnAirScheduleProps) => {
    const { store, params: routeParams } = props;
    if (!store) {
        return null;
    }

    const timeZone = store.site.index.timeZone || "";
    const now = store.env.TIME_OVERRIDE ? parseFloat(store.env.TIME_OVERRIDE) : Date.now();
    const [currentDay, updateDay] = useState(getCurrentDay(timeZone, now, routeParams?.day?.toLowerCase()));
    const slug = getOnAirSlug(store.site);

    return (
        <Container>
            <Tabs
                selectedTab={i18n.t(currentDay.day.short[0].toUpperCase() + currentDay.day.short[1])}
                onSelect={dayOfWeek => {
                    const newDay = daysOfWeek.find(d => i18n.t(d.short) === dayOfWeek.selectedTab.toLowerCase());
                    updateDay(getCurrentDay(timeZone, now, newDay ? newDay.full : ""));
                }}
            >
                {daysOfWeek.map((dayOfWeek, index) => (
                    <Tab
                        key={index}
                        label={i18n.t(dayOfWeek.short[0].toUpperCase() + dayOfWeek.short[1])}
                        active={currentDay.day.short === dayOfWeek.short}
                        onClick={() => {
                            if (typeof window !== "undefined") {
                                window.history.pushState(
                                    null,
                                    document.title,
                                    `/schedule/${dayOfWeek.full.toLowerCase()}/`,
                                );
                            }
                        }}
                    >
                        <Remote
                            cacheKey={`getOnairSchedule-${slug}-${dayOfWeek.full}`}
                            showLoading={true}
                            loader={() =>
                                getOnAirNowByDay({
                                    day: Sites_Onair_Day[dayOfWeek.short[0].toUpperCase() + dayOfWeek.short[1]],
                                    slug: store.site.index.slug,
                                    currentDate: now,
                                })
                            }
                        >
                            {({ data }) => {
                                return data?.schedule.length ? (
                                    <Schedule
                                        siteTimeZone={data.timeZone || store.site.index.timeZone || ""}
                                        selectedDay={dayOfWeek}
                                        current={data.current}
                                        schedule={data.schedule}
                                        block={props.block}
                                        store={props.store!}
                                    />
                                ) : (
                                    <OnairFallbackMessage />
                                );
                            }}
                        </Remote>
                    </Tab>
                ))}
            </Tabs>
        </Container>
    );
});

export default OnAirSchedule;
